import React, { useEffect, useMemo, useState } from 'react';
import { Heart, ThumbsUp, ThumbsDown, MessageSquare, Loader2, Trash2 } from 'lucide-react';
import { useAuthStore, useHousesStore } from '../../store/useStore.js';

const reactionOptions = [
  {
    value: 'love',
    label: 'Love it',
    icon: Heart,
    className: 'text-rose-500',
  },
  {
    value: 'thumbs-up',
    label: 'Looks good',
    icon: ThumbsUp,
    className: 'text-emerald-500',
  },
  {
    value: 'thumbs-down',
    label: 'Not for us',
    icon: ThumbsDown,
    className: 'text-amber-500',
  },
];

function ReactionButton({ isActive, Icon, label, onClick, className }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 border rounded-lg px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
        isActive
          ? 'border-indigo-500 bg-indigo-50 text-indigo-600'
          : 'border-gray-200 hover:border-indigo-200 text-gray-600'
      }`}
    >
      <Icon className={`w-4 h-4 ${className}`} />
      {label}
    </button>
  );
}

export default function HouseReactions({ house }) {
  const { user } = useAuthStore();
  const { addReaction, updateHouse, loadHouseById } = useHousesStore();

  const [notes, setNotes] = useState(house?.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [error, setError] = useState('');

  const reactionForUser = useMemo(() => {
    if (!user || !house?.reactions) return null;
    const entry = house.reactions.find((reaction) => reaction.user_id === user.id);
    return entry ? entry.reaction : null;
  }, [house?.reactions, user]);

  useEffect(() => {
    setNotes(house?.notes || '');
  }, [house?.notes]);

  const reactionSummary = useMemo(() => {
    const counts = house?.reactions?.reduce((acc, reaction) => {
      if (!reaction.reaction) return acc;
      acc[reaction.reaction] = (acc[reaction.reaction] || 0) + 1;
      return acc;
    }, {}) || {};

    return counts;
  }, [house?.reactions]);

  const handleReaction = async (value) => {
    if (!house) return;
    setError('');

    const nextValue = reactionForUser === value ? null : value;

    try {
      await addReaction(house.id, nextValue);
    } catch (err) {
      setError(err.message || 'Unable to update reaction.');
    }
  };

  const handleSaveNotes = async () => {
    if (!house) return;
    setSavingNotes(true);
    setError('');
    setNotesSaved(false);
    try {
      await updateHouse(house.id, { notes });
      await loadHouseById(house.id);
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2500);
    } catch (err) {
      setError(err.message || 'Unable to save notes.');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleClearNotes = async () => {
    if (!house || !notes) return;
    setNotes('');
    await handleSaveNotes();
  };

  return (
    <section className="space-y-6">
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-800">How does everyone feel?</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          {reactionOptions.map((option) => (
            <ReactionButton
              key={option.value}
              Icon={option.icon}
              label={option.label}
              className={option.className}
              isActive={reactionForUser === option.value}
              onClick={() => handleReaction(option.value)}
            />
          ))}
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {house?.reactions?.length > 0 && (
          <div className="text-sm text-gray-600 space-y-2">
            <div className="flex flex-wrap gap-2">
              {reactionOptions.map((option) => {
                const count = reactionSummary[option.value] || 0;
                if (!count) return null;
                return (
                  <span
                    key={option.value}
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-xs font-semibold"
                  >
                    {option.icon && React.createElement(option.icon, { className: `w-4 h-4 ${option.className}` })}
                    {count}
                  </span>
                );
              })}
            </div>
            <div>
              <p className="font-semibold text-gray-700">Household reactions:</p>
              <ul className="mt-1 space-y-1">
                {house.reactions.map((reaction) => {
                  const option = reactionOptions.find((opt) => opt.value === reaction.reaction);
                  if (!option) return null;
                  return (
                    <li key={reaction.user_id} className="flex items-center gap-2">
                      <span>
                        {option.icon && React.createElement(option.icon, { className: `w-4 h-4 ${option.className}` })}
                      </span>
                      <span className="text-gray-700 font-medium">{reaction.user_name || 'Household member'}</span>
                      <span className="text-gray-500 text-xs uppercase tracking-wide">{option.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <MessageSquare className="w-4 h-4 text-indigo-500" />
          Shared Notes
        </label>
        <textarea
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          rows={4}
          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="Add context for the rest of the household..."
        />
        <div className="flex flex-wrap gap-3 items-center">
          <button
            type="button"
            onClick={handleSaveNotes}
            disabled={savingNotes}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300"
          >
            {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
            Save Notes
          </button>
          <button
            type="button"
            onClick={handleClearNotes}
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
            disabled={!notes}
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </button>
          {notesSaved && <span className="text-sm text-emerald-600 font-medium">Saved!</span>}
        </div>
      </div>
    </section>
  );
}
